import { Add, Cancel } from '@mui/icons-material';
import {
  CircularProgress,
  Fade,
  IconButton,
  Paper,
  Popper,
  PopperPlacementType,
  TextField,
  Typography,
} from '@mui/material';
import { getDocumentsConciliationSuggestions } from 'lib/api/conciliations';
import { UpdateArrearRateOutput } from 'lib/api/document';
import FilterComponent from 'lib/common/FilterNonSearch';
import EnhancedTable from 'lib/designSystem/Table';
import {
  formatMoney,
  loggerDev,
  normalizeDate,
  showAlert,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AuthState } from '../../../../../../services/redux/authSlice';
import EditArrearDialog from '../../../Payments/components/PayIns/components/Conciliation/Conciliate/EditArrearDialog';
import DocumentAmountDetail from '../DocumentAmountDetail';
import CollectionsComponent from './components/CollectionsComponent';

type EditArrearDocument = {
  documentId: string;
  arrearStructure: {
    arrearType: string;
    arrearRate: number;
    arrearSegments: {
      min: number;
      max: number;
      value: number;
    }[];
  };
  hasConciliations: boolean;
} | null;

const DocumentsSuggestedContainer = (props: any) => {
  const {
    businessIdentifier,
    amountToApply,
    setAmountToApply,
    selected,
    setSelected,
    maxAmount,
    documents,
    setDocuments,
    setDocumentsToApply,
    addIconPlacement,
    disabled,
    documentsToApply,
    operationDate,
    preloadedDocuments,
    smallTextField,
  } = props;
  props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [openPopper, setOpenPopper] = React.useState(false);
  const [placement, setPlacement] =
    React.useState<PopperPlacementType>('right');
  const [filteredDocuments, setFilteredDocuments] = useState<any[]>([]);
  const [editArrearDocument, setEditArrearDocument] =
    useState<EditArrearDocument>(null);
  const [openEditArrearDialog, setOpenEditArrearDialog] = useState(false);

  const handleClickPopper =
    (newPlacement: PopperPlacementType) =>
    async (_event: React.MouseEvent<HTMLButtonElement>) => {
      loggerDev('click', placement, newPlacement);
      setOpenPopper((prev) => placement !== newPlacement || !prev);
      setPlacement(newPlacement);
      await setUp();
    };

  const handleClose = () => {
    setOpenPopper(false);
    setFilteredDocuments([]);
  };

  const handleChangeAmountToConciliate = (event: any) => {
    const amount = event.target.value.split('.').join('').split('$').join('');

    loggerDev('changing amount', amount, maxAmount);
    if (isNaN(amount)) return;

    if (amount > maxAmount) {
      alert('El monto a conciliar no puede ser mayor al monto a depositar');
      return;
    }

    const amountToApply = Number(amount);
    const document = documents.find((inv: any) => inv.id === event.target.name);
    if (
      amountToApply < 0 ||
      Math.ceil(amountToApply) > Math.ceil(document.totalRemaining)
    ) {
      return alert('El monto a conciliar no puede ser mayor al saldo restante');
    }
    document.amountToConciliate = amountToApply;
    setDocuments([...documents]);
    handleSelectChange(documents);
  };

  const cantSelectRow = (row: any) => {
    if (!maxAmount) return false;
    if (selected.includes(row.id)) return false;
    if (row.conciliationLog.length > 0) {
      const hasFutureConciliation =
        normalizeDate(
          new Date(
            row.conciliationLog[row.conciliationLog.length - 1].payInDate,
          ),
        ) > normalizeDate(new Date(operationDate));
      if (hasFutureConciliation) return true;
    }
    return Number(amountToApply) + Number(row.amountToConciliate) > maxAmount;
  };

  const getTooltip = (id: any) => {
    const row = documents.find((doc: any) => doc.id === id);
    return <DocumentAmountDetail document={row} />;
  };

  const handleEditArrearStructure = (documentId: string) => {
    const document = documents.find((doc: any) => doc.id === documentId);
    if (!document)
      return showAlert(dispatch, 'error', 'Documento no encontrado');
    const disabled =
      document.conciliationLog.length > 0
        ? document.conciliationLog[document.conciliationLog.length - 1].date >=
          operationDate
        : false;
    if (disabled)
      return showAlert(
        dispatch,
        'error',
        'No se puede editar la mora de este documento porque tiene conciliaciones futuras',
      );
    setEditArrearDocument({
      documentId: document.id,
      arrearStructure: document.arrearStructure,
      hasConciliations: document.conciliationLog.length > 0,
    });
    setOpenEditArrearDialog(true);
  };

  const handleSaveArrearStructure = (
    updatedFromApi?: UpdateArrearRateOutput,
  ) => {
    setEditArrearDocument(null);
    setOpenEditArrearDialog(false);

    if (updatedFromApi) {
      const { documentId, arrearStructure } = updatedFromApi;
      const applyArrearToDocument = (doc: any) =>
        doc.id === documentId ? { ...doc, arrearStructure } : doc;
      const updateList = (list: any[]) => list.map(applyArrearToDocument);
      setDocuments((prev: any[]) => updateList(prev));
      setFilteredDocuments((prev: any[]) => updateList(prev));
      return;
    }

    void setUp();
  };

  const documentsHeaders = [
    {
      key: 'collections',
      label: 'Cob.',
      customComponent: CollectionsComponent,
    },
    { key: 'folio', label: 'Folio' },

    { key: 'type', label: 'Tipo' },

    { key: 'businessName', label: 'Emisor' },
    { key: 'stakeholderName', label: 'Pagador' },

    {
      key: 'NCCancelDate',
      label: 'NC - Reclamo',
      type: 'NCCancelDate',
      canNull: true,
    },
    {
      key: 'paymentDate',
      label: 'Vencimiento',
      type: 'expiry-date-colored',
    },
    {
      key: 'arrearStructure',
      label: 'Tasa de mora',
      type: 'arrear-structure-editable',
      customAction: handleEditArrearStructure,
      extraData: {
        operationDate,
        user,
      },
    },
    {
      key: 'totalRemainingNet',
      label: 'Saldo',
      type: 'money',
      customTooltip: getTooltip,
    },
    {
      key: 'amountToConciliate',
      label: 'Monto a conciliar',
      handleChange: handleChangeAmountToConciliate,
      disabledFunction: cantSelectRow,
      minWidth: '130px',
      type: 'money',
    },
  ];

  const prepareDocuments = (documents: any) => {
    loggerDev('documents to apply', documentsToApply);
    return documents.map((document: any) => {
      const documentToApply = documentsToApply.find(
        (doc: any) => doc.id === document.id,
      );
      return {
        ...document,
        amountToConciliate: documentToApply
          ? documentToApply.amountToConciliate
          : maxAmount
            ? Math.min(maxAmount, document.totalRemainingNet)
            : document.totalRemainingNet,
      };
    });
  };

  const setUp = async () => {
    setLoading(true);
    try {
      if (preloadedDocuments && preloadedDocuments.length > 0) {
        const documentsPrepared = prepareDocuments(preloadedDocuments);
        setDocuments(documentsPrepared);
        setFilteredDocuments(documentsPrepared);
        setLoading(false);
        return;
      }

      const queryParams = `operationDate=${operationDate}`;
      const response = await getDocumentsConciliationSuggestions(
        dispatch,
        {
          businessIdentifier,
        },
        queryParams,
      );
      loggerDev('response', response);
      const documentsPrepared = prepareDocuments(response.documentsProcessed);
      setDocuments(documentsPrepared);
      setFilteredDocuments(documentsPrepared);
    } catch (error) {
      loggerDev(error);
    }
    setLoading(false);
  };

  const handleSelectChange = (newDocuments?: any) => {
    if (!documents.length || (newDocuments && !newDocuments.length)) return;
    const selectedDocuments = (newDocuments || documents).filter((inv: any) =>
      selected.includes(inv.id),
    );
    loggerDev('selectedDocuments', selectedDocuments);
    const amountToApply = selectedDocuments.reduce(
      (acc: number, inv: any) => acc + inv.amountToConciliate,
      0,
    );
    loggerDev(
      'amountToApplyaaa',
      amountToApply,
      maxAmount,
      amountToApply > maxAmount,
    );
    if (maxAmount && amountToApply > maxAmount) {
      alert('El monto a conciliar no puede ser mayor al monto a depositar');
      return;
    }
    setAmountToApply(String(amountToApply.toFixed(0)));
    setDocumentsToApply(selectedDocuments);
  };

  useEffect(() => {
    handleSelectChange();
  }, [selected]);

  const handleSetFitleredData = (filteredData: any) => {
    setFilteredDocuments(filteredData);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {addIconPlacement === 'end' && (
          <TextField
            size={smallTextField ? 'small' : 'medium'}
            value={formatMoney(amountToApply)}
            fullWidth
            placeholder="0"
            label="Documentos a aplicar ($)"
            disabled
            sx={{
              '& .MuiInputBase-input': {
                textAlign: 'right',
              },
            }}
          />
        )}
        <IconButton
          onClick={(e) => {
            void handleClickPopper('right')(e);
          }}
          disabled={!businessIdentifier || disabled}
        >
          <Add />
          {selected.length !== 0 ? (
            <Typography
              style={{
                position: 'absolute',
                bottom: -5,
                right: 0,
                color: 'white',
                fontSize: 10,
                padding: '2px 6px',
                borderRadius: 50,
                fontWeight: '400',
                margin: '1px 2px',
                backgroundColor: '#297dff',
              }}
            >
              {selected.length}
            </Typography>
          ) : null}
        </IconButton>
        {addIconPlacement !== 'end' && (
          <TextField
            value={formatMoney(amountToApply)}
            fullWidth
            placeholder="0"
            disabled
            size={smallTextField ? 'small' : 'medium'}
            sx={{
              '& .MuiInputBase-input': {
                textAlign: 'right',
              },
            }}
          />
        )}
      </div>
      <Popper
        style={{
          zIndex: 1300,
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '93%',
        }}
        open={openPopper}
        anchorEl={null}
        placement={'bottom'}
        transition={false}
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
        ]}
      >
        <Paper
          style={{
            padding: 20,
            maxHeight: 600,
            overflow: 'scroll',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center' }}>
              <Fade in={loading}>
                <CircularProgress />
              </Fade>
            </div>
          ) : (
            <div>
              {' '}
              <div
                style={{ display: 'flex', alignItems: 'center', width: '100%' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexGrow: 1,
                    padding: '10px 0px',
                  }}
                >
                  <Typography variant="h6">Documentos</Typography>
                  <FilterComponent
                    data={documents}
                    filterFields={[
                      { key: 'type', filterName: 'Tipo' },
                      { key: 'folio', filterName: 'Folio' },
                      { key: 'businessName', filterName: 'Emisor' },
                      { key: 'stakeholderName', filterName: 'Pagador' },
                      { key: 'operationId', filterName: 'ID operación' },
                    ]}
                    setFilteredData={handleSetFitleredData}
                  />
                </div>
                <IconButton
                  onClick={() => handleClose()}
                  style={{ marginLeft: 'auto' }}
                >
                  <Cancel />
                </IconButton>
              </div>
              <div>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'warning.main',
                    display: 'block',
                    marginBottom: 1,
                    fontStyle: 'italic',
                    fontSize: '0.75rem',
                  }}
                >
                  ⚠️ Los saldos y mora pendientes están calculados en base a la
                  fecha de operación
                </Typography>
              </div>
              <EnhancedTable
                dataHeaders={documentsHeaders}
                data={filteredDocuments}
                selected={selected}
                setSelected={setSelected}
                cantSelectRow={cantSelectRow}
                canSelectAllRows={true}
              />
            </div>
          )}
        </Paper>
      </Popper>
      {openEditArrearDialog && (
        <EditArrearDialog
          open={openEditArrearDialog}
          setOpen={setOpenEditArrearDialog}
          document={editArrearDocument}
          onSave={handleSaveArrearStructure}
          conciliationDate={new Date(operationDate)}
        />
      )}
    </div>
  );
};

export default DocumentsSuggestedContainer;
